import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import { useCallback } from "react";
import { AI_WORKSPACE_QUERY_KEYS } from "../constants";
import { AiWorkspaceService } from "../services/aiWorkspace.service";
import { ICreateAIWorkspacePayload, IAIWorkspace } from "../types";
import { BackednResponse } from "@shared/lib/types";

const QUERY_OPTIONS = {
  queryKey: [AI_WORKSPACE_QUERY_KEYS.SEARCH],
  skipInvalidation: true,
};

export function useCreateAiWorkspace(onSuccess?: (data: BackednResponse<IAIWorkspace>) => void) {
  const createAiWorkspace = useCallback(
    (payload: ICreateAIWorkspacePayload) =>
      AiWorkspaceService.createAIWorkspace(payload)
        .then((res) => {
          ToastService.success("AI Workspace created successfully");
          return res;
        })
        .catch(async (err) => {
          let errorMessage = "Failed to create AI Workspace";
          if (err?.response) {
            try {
              const responseBody = await err.response.json();
              if (responseBody?.msg) errorMessage = responseBody.msg;
            } catch (e) {}
          }
          ToastService.error(errorMessage);
          throw err;
        }),
    []
  );

  const {
    mutate: create,
    isLoading: isCreating,
    isSuccess: isCreated,
  } = useMutation<ICreateAIWorkspacePayload, BackednResponse<IAIWorkspace>>({
    mutationFn: createAiWorkspace,
    options: QUERY_OPTIONS,
    onSuccess: (queryClient, data) => {
      queryClient.setQueriesData(
        { queryKey: [AI_WORKSPACE_QUERY_KEYS.SEARCH] },
        (oldData: any) => {
          if (!oldData || !oldData.data || !oldData.data.data) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              data: [data.data, ...oldData.data.data],
              pagination: {
                ...oldData.data.pagination,
                totalCount: oldData.data.pagination.totalCount + 1
              }
            },
          };
        }
      );
      onSuccess?.(data);
    },
  });

  return {
    create,
    isCreating,
    isCreated,
  };
}
